import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { getAdminStatsAsync, postAdminDbCleanupAsync, postPermanentlyDeleteAllDataAsync } from '../../ajax/requests';
import Typography from '../../components/Typography';
import { alertGenericError } from '../../store/alerts/actions';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import './AdministratorDashboard.scss';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TextInputField } from '../../components/Forms/fields';
import { routes } from '../../ajax/routes';
import AppButton from '../../components/AppButton';

export const StatsTab = () => {
  const t = useTranslations()
  const dispatch = useDispatch()

  const [stats, setStats] = useState<string[][]|null|'failed'>(null)
  const [cleanupDbModalOpen, setCleanupDbModalOpen] = useState<boolean>(false)
  const [permanentlyDeleteAllDataModalOpen, setPermanentlyDeleteAllDataModalOpen] = useState<boolean>(false)
  const [permanentlyDeleteAllDataConfirmation, setPermanentlyDeleteAllDataConfirmation] = useState<string>('')
  const [cleaninUpDb, setCleaningUpDb] = useState<boolean>(false)

  const toggleCleanupDbModal = useCallback(() => setCleanupDbModalOpen(!cleanupDbModalOpen), [cleanupDbModalOpen])
  const togglePermanentlyDeleteAllDataModal = useCallback(() => setPermanentlyDeleteAllDataModalOpen(!permanentlyDeleteAllDataModalOpen), [permanentlyDeleteAllDataModalOpen])

  const cleanupDbModal = () => {
    setCleaningUpDb(true)
    postAdminDbCleanupAsync()
      .then(() => {toggleCleanupDbModal(); setStats(null)})
      .catch(() => dispatch(alertGenericError()))
      .finally(() => setCleaningUpDb(false))
  }

  const permanentlyDeleteAllDataModal = () => {
    setCleaningUpDb(true)
    postPermanentlyDeleteAllDataAsync(permanentlyDeleteAllDataConfirmation)
      .then(() => {togglePermanentlyDeleteAllDataModal(); setStats(null)})
      .catch(() => dispatch(alertGenericError()))
      .finally(() => setCleaningUpDb(false))
  }

  useEffect(() => {
    if (stats === null) {
      getAdminStatsAsync().then(res => setStats([
        [t(TK.totalAppointmentsThisMonth), `${res.totalAppointments.thisMonth}`],
        [t(TK.totalAppointmentsLastMonth), `${res.totalAppointments.lastMonth}`],
        [t(TK.totalSmsThisMonth), `${res.totalSmssSent.thisMonth}/${res.allowedSmss}`],
        [t(TK.totalSmsLastMonth), `${res.totalSmssSent.lastMonth}/${res.allowedSmss}`],
        [t(TK.totalEmailsThisMonth), `${res.totalEmailsSent.thisMonth}/${res.allowedEmails}`],
        [t(TK.totalEmailsLastMonth), `${res.totalEmailsSent.lastMonth}/${res.allowedEmails}`],
        [t(TK.documentsStorage), `${Math.floor(res.documentsStorage.usedBytes/1000000)}/${Math.floor(res.documentsStorage.allowedBytes/1000000)} MB`],
        [t(TK.databaseStorage), `${Math.floor(res.databaseStorage.usedBytes/1000000)}/${Math.floor(res.databaseStorage.allowedBytes/1000000)} MB`],
        [t(TK.totalUsers), `${res.totalUsers}/${res.allowedUsers}`],
        [t(TK.totalProfessionals), `${res.totalProfessionals}`],
        [t(TK.totalClients), `${res.totalClients}`],
      ])).catch((e) => {
        console.error(e)
        setStats('failed')
        dispatch(alertGenericError())
      })
    }
  }, [stats])

  const statsTables = React.useMemo(() => {
    if(stats === null || stats === 'failed') return null
    return isMobile ? [stats] : [
      [...stats].splice(0, Math.ceil(stats.length/2)), 
      [...stats].splice(Math.ceil(stats.length/2)), 
    ]
  }, [stats])

  return (
    <div style={{display: 'flex', gap: 'var(--spacer-S)', flexDirection: 'column'}}>
      <div className='statsTables'>
        {!statsTables ? <Typography>{stats === 'failed' ? 'Error' : t(TK.loading)}</Typography> : statsTables.map(table => (
          <div className='statsTable'>
            {table.map(([col1, col2], idx) => (<span key={idx}>
              <b>{col1}</b>
              <span>{col2}</span>
            </span>))}
          </div>
        ))}
      </div>
      
      <a href={routes.clientsExportCsv('name', 'email', 'phoneNumbers', 'city', 'birthday', 'taxNumber', 'subsystemNumber', 'notes')} download="clients.csv" >{t(TK.allClients)} CSV</a>

      <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
        <AppButton id='admin-stats-cleanup-db-button' onClick={toggleCleanupDbModal}>{t(TK.cleanupDb)}</AppButton>
        <AppButton id='admin-stats-permanently-delete-all-button' onClick={togglePermanentlyDeleteAllDataModal}>{t(TK.permanentlyDeleteAllData)}</AppButton>
      </div>

      <Modal isOpen={cleanupDbModalOpen} toggle={toggleCleanupDbModal}>
        <ModalHeader toggle={toggleCleanupDbModal}>{t(TK.cleanupDb)}</ModalHeader>
        <ModalBody>{t(TK.cleanupDbWarningMessage)}</ModalBody>
        <ModalFooter>
          <AppButton color="danger" onClick={cleanupDbModal} disabled={cleaninUpDb}>{t(TK.cleanupDb)}</AppButton>
          <AppButton onClick={toggleCleanupDbModal}>{t(TK.cancel)}</AppButton>
        </ModalFooter>
      </Modal>

      <Modal isOpen={permanentlyDeleteAllDataModalOpen} toggle={togglePermanentlyDeleteAllDataModal}>
        <ModalHeader toggle={togglePermanentlyDeleteAllDataModal}>{t(TK.permanentlyDeleteAllData)}</ModalHeader>
        <ModalBody>
          <Typography>{t(TK.permanentlyDeleteAllDataConfirmation)}</Typography>
          <TextInputField 
            id='admin-stats-permanently-delete-all-confirmation'
            placeholder='permanently delete all data'
            value={permanentlyDeleteAllDataConfirmation} 
            onChange={setPermanentlyDeleteAllDataConfirmation}
          />
        </ModalBody>
        <ModalFooter>
          <AppButton id='admin-stats-permanently-delete-all-confirm-button' color="danger" onClick={permanentlyDeleteAllDataModal} disabled={cleaninUpDb}>{t(TK.permanentlyDeleteAllData)}</AppButton>
          <AppButton onClick={togglePermanentlyDeleteAllDataModal}>{t(TK.cancel)}</AppButton>
        </ModalFooter>
      </Modal>
    </div>
  )
} 
